<script setup lang="ts">
import { getAnchorOffset } from '~/composables/useAnchorScroll'

const props = defineProps<{
  page: number
  pageSize: number
  total: number
  queryKey?: string
  scrollTarget?: string
  scrollBehavior?: ScrollBehavior
  hashBehavior?: 'keep' | 'clear' | 'target'
}>()

const totalPages = computed(() => Math.max(1, Math.ceil((props.total || 0) / (props.pageSize || 10))))
const route = useRoute()
const router = useRouter()
const pageQueryKey = computed(() => String(props.queryKey || 'page'))
const pageQueryValue = computed(() => String(route.query[pageQueryKey.value] || '1'))
const pagingScrollBehavior = computed<ScrollBehavior>(() => props.scrollBehavior || 'smooth')
const hashBehavior = computed(() => props.hashBehavior || 'clear')
const isNavigating = ref(false)

const resolveTargetPage = (target: number) => Math.min(totalPages.value, Math.max(1, target))
const resolveTargetHash = () => {
  if (hashBehavior.value === 'keep') {
    return route.hash || ''
  }
  if (hashBehavior.value === 'target' && props.scrollTarget?.startsWith('#')) {
    return props.scrollTarget
  }
  return ''
}
const toPage = (target: number) => {
  const next = resolveTargetPage(target)
  return {
    path: route.path,
    query: {
      ...route.query,
      [pageQueryKey.value]: String(next)
    },
    hash: resolveTargetHash()
  }
}

const waitForScrollSettled = async (targetY: number) => {
  if (!import.meta.client || pagingScrollBehavior.value !== 'smooth') {
    return
  }

  await new Promise<void>(resolve => {
    let rafId = 0
    let timeoutId = 0
    let nearTargetFrames = 0
    let resolved = false

    const cleanup = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId)
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }

    const done = () => {
      if (resolved) {
        return
      }
      resolved = true
      cleanup()
      resolve()
    }

    const tick = () => {
      if (resolved) {
        return
      }

      if (Math.abs(window.scrollY - targetY) <= 2) {
        nearTargetFrames += 1
      } else {
        nearTargetFrames = 0
      }

      if (nearTargetFrames >= 2) {
        done()
        return
      }

      rafId = window.requestAnimationFrame(tick)
    }

    rafId = window.requestAnimationFrame(tick)
    timeoutId = window.setTimeout(done, 1800)
  })
}

const scrollAfterPageChanged = async () => {
  if (!import.meta.client) {
    return
  }

  let targetY = 0
  if (props.scrollTarget) {
    const node = document.querySelector(props.scrollTarget)
    if (node) {
      targetY = Math.max(0, window.scrollY + node.getBoundingClientRect().top - getAnchorOffset())
      window.scrollTo({ top: targetY, behavior: pagingScrollBehavior.value })
      await waitForScrollSettled(targetY)
      return
    }
  }

  window.scrollTo({ top: 0, behavior: pagingScrollBehavior.value })
  await waitForScrollSettled(0)
}

// 标记是否是点击触发的，防止 watch 重复执行滚动
let isClickTriggered = false

const goPage = async (target: number) => {
  const next = resolveTargetPage(target)
  if (next === props.page || isNavigating.value) {
    return
  }
  isNavigating.value = true
  try {
    isClickTriggered = true
    await scrollAfterPageChanged()
    await router.push(toPage(next))
  } finally {
    isNavigating.value = false
  }
}

watch(pageQueryValue, (current, previous) => {
  if (!import.meta.client || current === previous) {
    return
  }

  // 如果是点击翻页触发的，刚才已经滚过了，这里重置状态直接 return
  if (isClickTriggered) {
    isClickTriggered = false
    return
  }

  // 处理用户点击浏览器前进/后退按钮时的滚动
  nextTick(() => {
    window.requestAnimationFrame(() => {
      scrollAfterPageChanged()
    })
  })
})
</script>

<template>
  <div v-if="totalPages > 1" class="pager">
    <button type="button" class="pager-link" :class="{ disabled: page <= 1 || isNavigating }" @click="goPage(page - 1)">上一页</button>

    <span>{{ page }} / {{ totalPages }}</span>

    <button type="button" class="pager-link" :class="{ disabled: page >= totalPages || isNavigating }" @click="goPage(page + 1)">下一页</button>
  </div>
</template>

<style scoped>
.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding: 12px 14px;
  border: 0;
  border-radius: 10px;
  background: var(--empty-bg);
}

.pager-link {
  margin: 0;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--brand);
  font: inherit;
  cursor: pointer;
  text-decoration: none;
}

.pager-link.disabled {
  pointer-events: none;
  opacity: 0.45;
}

.pager span {
  color: var(--text-soft);
  font-size: 14px;
}
</style>
